/**
 * JsonExplorer — Visualizador genérico de payloads JSON (padrão n8n)
 * -----------------------------------------------------------------------------
 * Componente PROVIDER-AGNÓSTICO usado para inspecionar o retorno de APIs
 * externas (node "Conexão" e, futuramente, node "Webhook").
 *
 * Modos de visualização:
 *   - Árvore : nós expansíveis, tipo/valor de cada campo, copiar caminho e
 *              mapear o campo para uma variável do fluxo.
 *   - JSON   : JSON formatado COM quebra de linha (resolve o corte lateral).
 *   - Tabela : quando o payload é um array de objetos, exibe chaves como colunas.
 *
 * Segurança/performance: profundidade e quantidade de nós renderizados são
 * limitadas para não travar a UI com payloads muito grandes.
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-29 v1.0.0 - Versão inicial.
 */

import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  Search,
  Braces,
  ListTree,
  Table as TableIcon,
  Variable,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { buildJsonTree, stringifyPathValue, type JsonTreeNode } from '@/lib/flow/jsonPaths';
import { toast } from 'sonner';

/** Limite de nós renderizados por vez em cada nível (evita travar a UI) */
const NODE_PAGE_SIZE = 60;

interface JsonExplorerProps {
  /** Payload retornado pelo provider */
  data: unknown;
  /** Chamado ao clicar em "Mapear" num campo da árvore */
  onSelectPath?: (path: string) => void;
  /** Altura máxima da área de conteúdo */
  maxHeightClass?: string;
}

/** Rótulo amigável do tipo de valor */
function typeLabel(node: JsonTreeNode): string {
  switch (node.type) {
    case 'object':
      return `objeto (${node.children?.length ?? 0})`;
    case 'array':
      return `lista (${node.children?.length ?? 0})`;
    case 'number':
      return 'número';
    case 'boolean':
      return 'booleano';
    case 'null':
      return 'nulo';
    default:
      return 'texto';
  }
}

/** Linha da árvore (recursiva) */
function TreeRow({
  node,
  depth,
  filter,
  expandedAll,
  onSelectPath,
  onCopy,
  copiedPath,
}: {
  node: JsonTreeNode;
  depth: number;
  filter: string;
  expandedAll: boolean | null;
  onSelectPath?: (path: string) => void;
  onCopy: (path: string) => void;
  copiedPath: string | null;
}) {
  const isBranch = node.type === 'object' || node.type === 'array';
  const [open, setOpen] = useState(depth < 2);
  const [visible, setVisible] = useState(NODE_PAGE_SIZE);

  // "Expandir tudo / recolher tudo" sobrepõe o estado local
  const isOpen = expandedAll === null ? open : expandedAll;

  const children = node.children ?? [];
  const shown = children.slice(0, visible);

  return (
    <div className="min-w-0">
      <div
        className="group flex items-start gap-1.5 rounded px-1 py-0.5 hover:bg-muted/60"
        style={{ paddingLeft: `${depth * 12 + 4}px` }}
      >
        {isBranch ? (
          <button
            type="button"
            onClick={() => setOpen(!isOpen)}
            className="mt-0.5 shrink-0 text-muted-foreground hover:text-foreground"
            aria-label={isOpen ? 'Recolher' : 'Expandir'}
          >
            {isOpen ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </button>
        ) : (
          <span className="mt-0.5 w-3.5 shrink-0" />
        )}

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="font-mono text-[11px] font-medium text-foreground break-all">
              {node.key}
            </span>
            <span className="text-[10px] text-muted-foreground">{typeLabel(node)}</span>
            {!isBranch && (
              <span className="font-mono text-[11px] text-muted-foreground break-all whitespace-pre-wrap">
                {stringifyPathValue(node.value) || '—'}
              </span>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            title="Copiar caminho"
            onClick={() => onCopy(node.path)}
          >
            {copiedPath === node.path ? (
              <Check className="h-3 w-3 text-primary" />
            ) : (
              <Copy className="h-3 w-3" />
            )}
          </Button>
          {onSelectPath && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              title="Mapear para variável"
              onClick={() => onSelectPath(node.path)}
            >
              <Variable className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>

      {isBranch && isOpen && (
        <div className="min-w-0">
          {shown.map((child) => (
            <TreeRow
              key={child.path}
              node={child}
              depth={depth + 1}
              filter={filter}
              expandedAll={expandedAll}
              onSelectPath={onSelectPath}
              onCopy={onCopy}
              copiedPath={copiedPath}
            />
          ))}
          {children.length > visible && (
            <button
              type="button"
              className="ml-6 text-[11px] text-primary hover:underline"
              onClick={() => setVisible((v) => v + NODE_PAGE_SIZE)}
            >
              Mostrar mais ({children.length - visible} restantes)
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function JsonExplorer({
  data,
  onSelectPath,
  maxHeightClass = 'max-h-72',
}: JsonExplorerProps) {
  const [filter, setFilter] = useState('');
  const [expandedAll, setExpandedAll] = useState<boolean | null>(null);
  const [copiedPath, setCopiedPath] = useState<string | null>(null);

  /** Árvore completa do payload */
  const tree = useMemo(() => buildJsonTree(data), [data]);

  /**
   * Filtro: mantém apenas ramos que casam com a busca (chave, caminho ou valor).
   * Aplicado de forma recursiva e pura (não altera a árvore original).
   */
  const filteredTree = useMemo(() => {
    const term = filter.trim().toLowerCase();
    if (!term) return tree;

    const matches = (node: JsonTreeNode): JsonTreeNode | null => {
      const selfMatch =
        node.key.toLowerCase().includes(term) ||
        node.path.toLowerCase().includes(term) ||
        stringifyPathValue(node.value).toLowerCase().includes(term);

      const kids = (node.children ?? [])
        .map(matches)
        .filter((n): n is JsonTreeNode => n !== null);

      if (selfMatch || kids.length > 0) {
        return { ...node, children: kids.length > 0 ? kids : node.children };
      }
      return null;
    };

    return tree.map(matches).filter((n): n is JsonTreeNode => n !== null);
  }, [tree, filter]);

  /** Detecta array de objetos para o modo Tabela */
  const tableRows = useMemo(() => {
    if (Array.isArray(data) && data.every((r) => r && typeof r === 'object' && !Array.isArray(r))) {
      return data as Record<string, unknown>[];
    }
    return null;
  }, [data]);

  const tableColumns = useMemo(() => {
    if (!tableRows) return [];
    const cols = new Set<string>();
    tableRows.slice(0, 50).forEach((row) => Object.keys(row).forEach((k) => cols.add(k)));
    return Array.from(cols).slice(0, 20);
  }, [tableRows]);

  const handleCopy = (path: string) => {
    void navigator.clipboard.writeText(path);
    setCopiedPath(path);
    toast.success('Caminho copiado');
    window.setTimeout(() => setCopiedPath(null), 1500);
  };

  const rawJson = useMemo(() => {
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  }, [data]);

  return (
    <div className="rounded-lg border border-border">
      <Tabs defaultValue="tree">
        <div className="flex flex-wrap items-center gap-2 border-b border-border p-2">
          <TabsList className="h-8">
            <TabsTrigger value="tree" className="h-6 gap-1 text-[11px]">
              <ListTree className="h-3 w-3" />
              Árvore
            </TabsTrigger>
            <TabsTrigger value="json" className="h-6 gap-1 text-[11px]">
              <Braces className="h-3 w-3" />
              JSON
            </TabsTrigger>
            {tableRows && (
              <TabsTrigger value="table" className="h-6 gap-1 text-[11px]">
                <TableIcon className="h-3 w-3" />
                Tabela
              </TabsTrigger>
            )}
          </TabsList>

          <div className="relative min-w-[140px] flex-1">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Buscar campo ou valor"
              maxLength={80}
              className="h-8 pl-7 text-xs"
            />
          </div>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 text-[11px]"
            onClick={() => setExpandedAll(expandedAll === true ? false : true)}
          >
            {expandedAll === true ? 'Recolher tudo' : 'Expandir tudo'}
          </Button>
        </div>

        <TabsContent value="tree" className="m-0">
          <ScrollArea className={cn(maxHeightClass, 'w-full')}>
            <div className="p-2">
              {filteredTree.length === 0 ? (
                <p className="p-2 text-xs text-muted-foreground">
                  Nenhum campo encontrado para a busca.
                </p>
              ) : (
                filteredTree.map((node) => (
                  <TreeRow
                    key={node.path}
                    node={node}
                    depth={0}
                    filter={filter}
                    expandedAll={expandedAll}
                    onSelectPath={onSelectPath}
                    onCopy={handleCopy}
                    copiedPath={copiedPath}
                  />
                ))
              )}
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="json" className="m-0">
          <div className="flex items-center justify-end border-b border-border px-2 py-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-[11px]"
              onClick={() => {
                void navigator.clipboard.writeText(rawJson);
                toast.success('JSON copiado');
              }}
            >
              <Copy className="mr-1 h-3 w-3" />
              Copiar JSON
            </Button>
          </div>
          <ScrollArea className={cn(maxHeightClass, 'w-full')}>
            {/* Quebra de linha garantida: resolve o corte lateral do payload */}
            <pre className="whitespace-pre-wrap break-all p-2 font-mono text-[11px] leading-relaxed">
              {rawJson}
            </pre>
          </ScrollArea>
        </TabsContent>

        {tableRows && (
          <TabsContent value="table" className="m-0">
            <ScrollArea className={cn(maxHeightClass, 'w-full')}>
              <div className="min-w-full p-2">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="border-b border-border text-left">
                      {tableColumns.map((col) => (
                        <th key={col} className="px-2 py-1 font-medium text-muted-foreground">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.slice(0, 50).map((row, idx) => (
                      <tr key={idx} className="border-b border-border/60 align-top">
                        {tableColumns.map((col) => (
                          <td key={col} className="max-w-[220px] px-2 py-1 break-words">
                            {stringifyPathValue(row[col])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {tableRows.length > 50 && (
                  <p className="p-2 text-[10px] text-muted-foreground">
                    Exibindo 50 de {tableRows.length} registros.
                  </p>
                )}
              </div>
            </ScrollArea>
          </TabsContent>
        )}
      </Tabs>

      <div className="flex items-center justify-between border-t border-border px-2 py-1">
        <Badge variant="secondary" className="text-[10px]">
          {tree.length} campos no primeiro nível
        </Badge>
        {onSelectPath && (
          <span className="text-[10px] text-muted-foreground">
            Clique no ícone de variável para mapear um campo
          </span>
        )}
      </div>
    </div>
  );
}
